import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePresentation } from '../hooks/usePresentation.js';
import ToolHeader from '../components/ToolHeader.jsx';
import { LEVELS, QUESTIONS, recommendLevel } from './objectiveLevels.js';
import './Objective.css';

/**
 * 03 · Write your objective
 *
 * The Objective Framer — the main view.
 *   1. Pick a Bloom's level from a dropdown
 *   2. Pick an action verb from a dropdown (filtered by level)
 *   3. Type the skill / task
 *   4. "Build my objective" generates the full sentence
 *
 * Plus: a tucked-in "Need help figuring out what to present?" disclosure
 * that opens the 4-question targeter wizard. The wizard's recommendation
 * auto-fills the Bloom's level dropdown.
 */

const PREFIX = 'By the end of this presentation, participants will be able to';
const LEVEL_KEYS = ['remember', 'understand', 'apply', 'analyse', 'evaluate', 'create'];

export default function Objective() {
  const { data, updateTool, savedFlash } = usePresentation();
  const navigate = useNavigate();
  const stored = data.objective ?? {};

  const [level, setLevel]         = useState(stored.level ?? '');
  const [verb, setVerb]           = useState(stored.verb ?? '');
  const [otherVerb, setOtherVerb] = useState(stored.otherVerb ?? '');
  const [skill, setSkill]         = useState(stored.skill ?? '');
  const [built, setBuilt]         = useState(stored.built ?? null);

  // Targeter wizard local state
  const [targeterOpen, setTargeterOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    q1: stored.q1 ?? null,
    q2: stored.q2 ?? null,
    q3: stored.q3 ?? null,
    q4: stored.q4 ?? null,
  });
  const [showWizardResult, setShowWizardResult] = useState(false);

  // Persist on change
  useEffect(() => {
    updateTool('objective', { level, verb, otherVerb, skill, built, ...answers });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, verb, otherVerb, skill, built, answers]);

  const lvl = level ? LEVELS[level] : null;
  const verbList = lvl ? lvl.verbs : [];
  const effectiveVerb = verb === 'other' ? otherVerb.trim() : verb;
  const canBuild = !!level && !!effectiveVerb && skill.trim().length > 0;

  function handleLevelChange(val) {
    setLevel(val);
    setVerb('');
    setOtherVerb('');
    setBuilt(null); // clear stale result
  }
  function handleVerbChange(val) {
    setVerb(val);
    if (val !== 'other') setOtherVerb('');
    setBuilt(null);
  }
  function handleSkillChange(val) {
    setSkill(val);
    setBuilt(null);
  }

  function buildObjective() {
    if (!canBuild) return;
    setBuilt({
      prefix: PREFIX,
      verb: effectiveVerb,
      task: skill.trim().replace(/\.+$/, ''), // strip trailing periods
    });
    // Scroll to result after render
    setTimeout(() => {
      document.getElementById('obj-result')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  function startOver() {
    setLevel(''); setVerb(''); setOtherVerb(''); setSkill(''); setBuilt(null);
  }

  // ───────── targeter handlers ─────────
  function selectAnswer(qid, value) {
    setAnswers((a) => ({ ...a, [qid]: value }));
  }
  function targeterFinish() {
    const recommended = recommendLevel(answers);
    setShowWizardResult(true);
    // Pre-set the level so user can confirm
    setLevel(recommended);
    setVerb('');
    setOtherVerb('');
    setBuilt(null);
  }
  function useThisLevel() {
    setShowWizardResult(false);
    setTargeterOpen(false);
    setStep(0);
  }
  function restartTargeter() {
    setAnswers({ q1: null, q2: null, q3: null, q4: null });
    setStep(0);
    setShowWizardResult(false);
  }

  return (
    <div className="objective">
      <ToolHeader num="02" title="Write your objective" savedFlash={savedFlash} />

      <div className="obj-body">

        {/* ═══ Targeter disclosure ═══ */}
        <div className="obj-targeter-wrap">
          <button
            type="button"
            className={`obj-cta-help ${targeterOpen ? 'is-open' : ''}`}
            onClick={() => setTargeterOpen((o) => !o)}
          >
            <span className="obj-cta-help-icon">?</span>
            <span>Need help figuring out what to present?</span>
            <span className="obj-cta-help-chevron">▾</span>
          </button>

          {targeterOpen && (
            <div className="obj-targeter-panel">
              {!showWizardResult ? (
                <Wizard
                  step={step}
                  answers={answers}
                  onAnswer={selectAnswer}
                  onNext={() => setStep((s) => s + 1)}
                  onBack={() => (step === 0 ? setTargeterOpen(false) : setStep((s) => s - 1))}
                  onFinish={targeterFinish}
                />
              ) : (
                <WizardResult
                  level={LEVELS[level]}
                  onUseLevel={useThisLevel}
                  onRestart={restartTargeter}
                />
              )}
            </div>
          )}
        </div>

        {/* ═══ Framer card ═══ */}
        <div className="obj-framer">
          <header className="obj-framer-head">
            <span className="eyebrow" style={{ color: 'var(--forest-mid)' }}>The performance statement</span>
            <h2>What will participants be able to do?</h2>
            <p>
              A presentation objective isn't about what you'll cover — it's about what
              participants will be able to <em>do</em> because of it. Start with the
              level of thinking you're asking for, then name the action.
            </p>
          </header>

          <div className="obj-framer-body">

            <div className="obj-field">
              <label htmlFor="obj-level" className="obj-field-label">Bloom's Level</label>
              <select
                id="obj-level"
                value={level}
                onChange={(e) => handleLevelChange(e.target.value)}
                className="obj-select"
              >
                <option value="" disabled>— Select level —</option>
                {LEVEL_KEYS.map((k) => (
                  <option key={k} value={k}>{LEVELS[k].num} · {LEVELS[k].name}</option>
                ))}
              </select>
            </div>

            <div className="obj-field">
              <label htmlFor="obj-verb" className="obj-field-label">Action Verb</label>
              <select
                id="obj-verb"
                value={verb}
                onChange={(e) => handleVerbChange(e.target.value)}
                className="obj-select"
                disabled={!level}
              >
                <option value="" disabled>{level ? '— Select a verb —' : '— Select level first —'}</option>
                {verbList.map((v) => (
                  <option key={v} value={v}>{v}</option>
                ))}
                {level && <option value="other">other…</option>}
              </select>

              {verb === 'other' && (
                <div className="obj-other-row">
                  <input
                    type="text"
                    value={otherVerb}
                    onChange={(e) => { setOtherVerb(e.target.value); setBuilt(null); }}
                    placeholder="Type your verb…"
                    className="obj-input"
                    autoFocus
                  />
                  <span className="obj-hint-text">Any action verb not in the list above.</span>
                </div>
              )}
            </div>

            <div className="obj-field">
              <label htmlFor="obj-skill" className="obj-field-label">Skill / Task Description</label>
              <input
                id="obj-skill"
                type="text"
                value={skill}
                onChange={(e) => handleSkillChange(e.target.value)}
                placeholder="e.g. the diamond of participation in their next council meeting"
                className="obj-input"
                onKeyDown={(e) => { if (e.key === 'Enter') buildObjective(); }}
              />
              <span className="obj-hint-text">
                Complete the sentence: "participants will be able to {verb && verb !== 'other' ? <strong>{verb}</strong> : '[verb]'} ___________"
              </span>
            </div>

          </div>

          <div className="obj-framer-foot">
            <button
              type="button"
              className="btn btn-primary obj-build-btn"
              onClick={buildObjective}
              disabled={!canBuild}
            >
              Build my objective →
            </button>
          </div>
        </div>

        {/* ═══ Result ═══ */}
        {built && (
          <div id="obj-result" className="obj-result">
            <div className="obj-result-head">
              <span className="eyebrow" style={{ color: 'rgba(255,255,255,0.78)' }}>Your presentation objective</span>
            </div>
            <div className="obj-result-display">
              <span className="obj-r-prefix">{built.prefix}</span>{' '}
              <span className="obj-r-verb">{built.verb}</span>{' '}
              <span className="obj-r-task">{built.task}.</span>
            </div>
            <div className="obj-result-divider" />
            <div className="obj-result-copy">
              <span className="eyebrow">Plain text — tap to select</span>
              <textarea
                readOnly
                rows={3}
                className="obj-copy-textarea"
                value={`${built.prefix} ${built.verb} ${built.task}.`}
                onClick={(e) => e.target.select()}
              />
              <div className="obj-result-actions">
                <button type="button" className="btn-text" onClick={startOver}>↺ Start over</button>
                <button
                  type="button"
                  className="btn-text"
                  onClick={() => {
                    const text = `${built.prefix} ${built.verb} ${built.task}.`;
                    navigator.clipboard?.writeText(text).then(
                      () => alert('Objective copied to clipboard.'),
                      () => alert('Could not copy.')
                    );
                  }}
                >
                  Copy
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary obj-continue"
          onClick={() => navigate('/tools/draft')}
        >
          Continue to Draft →
        </button>

      </div>
    </div>
  );
}

/* ════════════════════════════════ WIZARD (4 questions) ════════════════════════════════ */

function Wizard({ step, answers, onAnswer, onNext, onBack, onFinish }) {
  const q = QUESTIONS[step];
  const selected = answers[q.id];
  const isLast = step === QUESTIONS.length - 1;

  return (
    <>
      <div className="obj-progress">
        <div className="obj-progress-track">
          <div className="obj-progress-fill" style={{ width: `${(step / QUESTIONS.length) * 100}%` }} />
        </div>
        <span className="eyebrow obj-progress-label">Question {step + 1} of {QUESTIONS.length}</span>
      </div>

      <div className="obj-step">
        <div className="obj-step-num">
          <span className="obj-step-badge">{q.badge}</span>
          {q.label}
        </div>
        <h3 className="obj-step-q">{q.question}</h3>
        <p className="obj-step-sub">{q.sub}</p>
        {q.hint && <div className="obj-hint-callout">{q.hint}</div>}
        <div className="obj-choices">
          {q.options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onAnswer(q.id, opt.value)}
              className={`obj-choice ${selected === opt.value ? 'is-selected' : ''}`}
            >
              <strong>{opt.title}</strong>
              <span>{opt.desc}</span>
            </button>
          ))}
        </div>
        <div className="obj-nav">
          <button type="button" className="btn-text" onClick={onBack}>
            {step === 0 ? '← Close' : '← Back'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={isLast ? onFinish : onNext}
            disabled={!selected}
          >
            {isLast ? 'See recommendation →' : 'Next →'}
          </button>
        </div>
      </div>
    </>
  );
}

/* ════════════════════════════════ WIZARD RESULT ════════════════════════════════ */

function WizardResult({ level, onUseLevel, onRestart }) {
  if (!level) return null;
  return (
    <div className="obj-wizard-result" style={{ background: level.bg, borderColor: level.border }}>
      <span className="eyebrow" style={{ color: level.color }}>
        Recommended · Level {level.num} of 6
      </span>
      <h3 className="obj-wizard-result-name" style={{ color: level.textDark }}>{level.name}</h3>
      <p className="obj-wizard-result-tagline">{level.tagline}</p>
      <div className="obj-wizard-result-actions">
        <button type="button" className="btn-text" onClick={onRestart}>↺ Start over</button>
        <button type="button" className="btn btn-primary" onClick={onUseLevel}>
          Use this level →
        </button>
      </div>
    </div>
  );
}
